/* src/Pages/SlotsPage/index.tsx */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Card, Space, message, Spin, Progress, Alert, Typography } from 'antd';
import type { InputRef } from 'antd/es/input/Input';

const { Text } = Typography;
import type { Key } from 'react';

import {
  createSlot,
  updateSlot,
  batchExtend,
  batchRefund,
  fetchAllSlots,
  deleteSlot,
  batchUpdateSlots,
} from '../../Modules/Slot/Presentation/actions';

import { CreateSlotDTO, Slot, SlotSearchParams } from '../../Modules/Slot/Model/Slot';
import SlotTable from './components/SlotTable';
import ManagementBar from './components/ManagementBar';
import SlotAddModal from './components/SlotAddModal';
import SlotCloneModal from './components/SlotCloneModal';
import SlotExtendModal from './components/SlotExtendModal';
import SlotEditModal from './components/SlotEditModal';
import SlotRefundModal from './components/SlotRefundModal';
import ExcelImportModal from './components/ExcelImportModal';
import ExcelBatchUpdateModal from './components/ExcelBatchUpdateModal';

import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';
import { useSlots } from '../../Modules/Slot/Presentation/hooks/hooks';
import { selectUser } from '../../Modules/User/Presentation/selectors';
import { selectAccounts } from '../../Modules/Account/Presentation/selectors';
import { 
  selectDefaultPageSlots, 
  selectDefaultPagePagination, 
  selectDefaultPageLoaded 
} from '../../Modules/Slot/Presentation/selectors';
import {
  setDefaultPage,
  clearDefaultPage,
  beginOptimisticSlotMerge,
  beginOptimisticBatchExtend,
  beginOptimisticBatchRefund,
  beginOptimisticCreateSlots,
  beginOptimisticDeleteSlots,
} from '../../Modules/Slot/Presentation/reducers';
import { prepareOptimisticBatchCreate } from '../../Modules/Slot/Presentation/buildOptimisticSlot';
import { isOptimisticInlineDto } from '../../Modules/Slot/Presentation/optimisticInline';
import { fetchAccounts } from '../../Modules/Account/Presentation/actions';
import useOnce from '../../Utils/hook/useOnce';
import SlotBulkEditModal from './components/SlotBulkEditModal';
import { assertAdminOrManager } from '../../Modules/Slot/Domain/UseCase/Utils/Permission';
import { batchCreateSlots } from '../../Modules/Slot/Presentation/actions';
import SlotOpsOverrideBanner from '../../components/SlotOpsOverrideBanner';
import {
  dtoTriggersRankRecalc,
  expectsAsyncRank,
} from '../../Utils/slotRankAsync';
import { scheduleSlotRankPolling } from '../../Utils/slotRankPolling';
import { api } from '../../api/axiosInstance';

const SlotsPage: React.FC = () => {
  /** ---------------------------------------------------------------- */
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectUser);   // 아직 null 일 수 있음
  const accounts = useAppSelector(selectAccounts);
  
  // 컴포넌트 최상단 ref
  const topRef = useRef<HTMLDivElement>(null);

  /* ==============  검색/필터 상태 ============== */
  const [searchParams, setSearchParams] = useState<SlotSearchParams>({
    page: 1,
    pageSize: 20,
    active: 'true',
    order_by: 'registered',
    order_dir: 'desc'
  });

  const [searchValue, setSearchValue] = useState(''); // 실제 검색에 사용될 값
  const [searchField, setSearchField] = useState<'slot' | 'keyword' | 'url' | 'user' | 'creator' | 'mid_single' | 'mid_compare'>('slot');
  const [localFindOpen, setLocalFindOpen] = useState(false);
  const [localFindQuery, setLocalFindQuery] = useState('');
  const [localFindMatchCount, setLocalFindMatchCount] = useState(0);
  const [localFindActiveMatchIndex, setLocalFindActiveMatchIndex] = useState(-1);
  const [localFindFocusNonce, setLocalFindFocusNonce] = useState(0);
  const localFindInputRef = useRef<InputRef>(null);

  // 검색 필터 변경 시 키워드 초기화 및 기본 페이지로 리셋
  const handleSearchFieldChange = useCallback((field: 'slot' | 'keyword' | 'url' | 'user' | 'creator' | 'mid_single' | 'mid_compare') => {
    setSearchField(field);
    setSearchValue('');
    
    // 검색 필터 변경 시 기본 페이지로 리셋하되 현재 페이지 크기는 유지
    setSearchParams(prev => ({
      page: 1,
      pageSize: prev.pageSize, // 현재 페이지 크기 유지
      active: 'true',
      order_by: prev.order_by || 'registered',
      order_dir: prev.order_dir || 'desc'
    }));
  }, []);

  const openLocalFind = useCallback(() => {
    setLocalFindOpen(true);
    setLocalFindFocusNonce(prev => prev + 1);
  }, []);

  const closeLocalFind = useCallback(() => {
    setLocalFindOpen(false);
  }, []);

  const handleLocalFindChange = useCallback((value: string) => {
    setLocalFindQuery(value);
    setLocalFindActiveMatchIndex(value.trim() ? 0 : -1);
  }, []);

  const handleLocalFindNavigate = useCallback((direction: 1 | -1) => {
    if (!localFindQuery.trim() || localFindMatchCount === 0) return;

    setLocalFindActiveMatchIndex(prev => {
      if (prev < 0 || prev >= localFindMatchCount) {
        return direction === 1 ? 0 : localFindMatchCount - 1;
      }

      if (direction === 1) {
        return prev === localFindMatchCount - 1 ? 0 : prev + 1;
      }

      return prev === 0 ? localFindMatchCount - 1 : prev - 1;
    });
  }, [localFindMatchCount, localFindQuery]);

  /* ==============  UI 상태 ============== */
  const [selected, setSelected] = useState<Key[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [extOpen, setExtOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [excelUploading, setExcelUploading] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [excelImportFile, setExcelImportFile] = useState<File | null>(null);
  const [excelBatchUpdateOpen, setExcelBatchUpdateOpen] = useState(false);
  const [excelBatchUpdateFile, setExcelBatchUpdateFile] = useState<File | null>(null);

  /* ==============  분할 업로드 상태 ============== */
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [batchUploadTotal, setBatchUploadTotal] = useState(0);
  const [batchUploadCurrent, setBatchUploadCurrent] = useState(0);
  const [batchUploadChunkTotal, setBatchUploadChunkTotal] = useState(0);
  const [batchUploadChunkCurrent, setBatchUploadChunkCurrent] = useState(0);
  const [batchUploadWaitSeconds, setBatchUploadWaitSeconds] = useState(0);

  /* ==============  새로고침 방지  ============== */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isBatchUploading) {
        e.preventDefault();
        e.returnValue = ''; // 크롬에서 경고창을 띄우기 위해 필요
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isBatchUploading]);

  useEffect(() => {
    const handleLocalFindShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        openLocalFind();
      }
    };

    window.addEventListener('keydown', handleLocalFindShortcut);
    return () => window.removeEventListener('keydown', handleLocalFindShortcut);
  }, [openLocalFind]);

  useEffect(() => {
    if (!localFindOpen) return;

    const timer = window.setTimeout(() => {
      localFindInputRef.current?.focus({ cursor: 'all' });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [localFindOpen, localFindFocusNonce]);

  useEffect(() => {
    if (!localFindOpen || !localFindQuery.trim() || localFindMatchCount === 0) {
      setLocalFindActiveMatchIndex(prev => (prev === -1 ? prev : -1));
      return;
    }

    setLocalFindActiveMatchIndex(prev => {
      if (prev < 0 || prev >= localFindMatchCount) return 0;
      return prev;
    });
  }, [localFindMatchCount, localFindOpen, localFindQuery]);

  /** me 가 바뀔 때마다 슬롯 훅 */
  const { slots, loading, error, pagination, filters, refresh } = useSlots(me!, searchParams);

  // 기본 페이지 캐시
  const defaultPageSlots = useAppSelector(selectDefaultPageSlots);
  const defaultPagePagination = useAppSelector(selectDefaultPagePagination);
  const defaultPageLoaded = useAppSelector(selectDefaultPageLoaded);

  // 기본 페이지인지 확인 (검색 조건 없음 + 1페이지 + pageSize=20 + 기본 정렬 registered desc)
  const isDefaultPage = useMemo(() => 
    !searchValue.trim() && 
    (!searchParams.slotId && !searchParams.keyword && !searchParams.url && !searchParams.sellerId &&
     !searchParams.creatorId && !searchParams.mid_single && !searchParams.mid_compare) && 
    searchParams.page === 1 &&
    searchParams.pageSize === 20 &&
    (searchParams.order_by === undefined || searchParams.order_by === 'registered') &&
    (searchParams.order_dir === undefined || searchParams.order_dir === 'desc'),
    [searchValue, searchParams]
  );

  // 기본 페이지 데이터를 캐시에 저장
  useEffect(() => {
    if (isDefaultPage && slots.length > 0 && !defaultPageLoaded) {
      dispatch(setDefaultPage({ slots, pagination }));
    }
  }, [isDefaultPage, slots, pagination, defaultPageLoaded, dispatch]);

  // 기본 페이지일 때 캐시된 데이터 사용 (단, needsRefresh가 true이면 캐시 무시)
  const needsRefresh = useAppSelector((state) => state.slot.needsRefresh);
  const displaySlots = useMemo(() => {
    const useCache = isDefaultPage && defaultPageLoaded && !needsRefresh;
    return useCache ? defaultPageSlots : slots;
  }, [isDefaultPage, defaultPageLoaded, defaultPageSlots, slots, needsRefresh]);
  
  const displayPagination = useMemo(() => 
    isDefaultPage && defaultPageLoaded && !needsRefresh ? defaultPagePagination : pagination,
    [isDefaultPage, defaultPageLoaded, defaultPagePagination, pagination, needsRefresh]
  );

  /* ➜ 최초 한 번: 계정 목록, 슬롯 목록 */
  useOnce(() => { if (accounts.length === 0) dispatch(fetchAccounts()); });
  useOnce(() => { refresh(); });

  /* ==============  검색 파라미터 업데이트 ============== */
  const updateSearchParams = useCallback((newParams: Partial<SlotSearchParams>) => {
    setSearchParams(prev => ({
      ...prev,
      ...newParams,
      page: 1 // 검색 조건이 변경되면 첫 페이지로
    }));
  }, []);

  // 정렬 변경 시: 캐시 무효화 + 즉시 1페이지 조회되도록 파라미터 변경
  const handleSortByChange = useCallback((v: 'registered' | 'start_date' | 'end_date') => {
    dispatch(clearDefaultPage());
    updateSearchParams({ order_by: v });
  }, [dispatch, updateSearchParams]);

  const handleSortDirChange = useCallback((v: 'asc' | 'desc') => {
    dispatch(clearDefaultPage());
    updateSearchParams({ order_dir: v });
  }, [dispatch, updateSearchParams]);

  // 검색 실행: 검색어와 현재 필터 상태를 함께 반영
  const handleSearch = useCallback((value: string) => {
    const trimmed = value.trim();
    setSearchValue(value);
    
    // 검색어가 비어있으면 검색 파라미터 초기화하되 페이지 크기는 유지
    if (!trimmed) {
      setSearchParams(prev => ({
        page: 1,
        pageSize: prev.pageSize, // 현재 페이지 크기 유지
        active: 'true',
        order_by: prev.order_by || 'registered',
        order_dir: prev.order_dir || 'desc'
      }));
      return;
    }

    const params: Partial<SlotSearchParams> = {};
    
    // 검색어가 있을 때만 검색 조건 추가
    switch (searchField) {
      case 'slot': {
        const slotId = parseInt(trimmed);
        if (!isNaN(slotId)) {
          params.slotId = slotId;
        } else {
          // 숫자가 아닌 값이 들어오면 -1을 넣어 빈 결과 유도 및 메시지 표시
          params.slotId = -1;
          message.warning('올바른 슬롯 번호를 입력해주세요.');
        }
        break;
      }
      case 'keyword':
        params.keyword = trimmed;
        break;
      case 'url':
        params.url = trimmed;
        break;
      case 'user': {
        const trimmed = value.trim();
        const searchLower = trimmed.toLowerCase();
        const sellersOnly = accounts.filter(acc => acc.grade === 'SELLER');

        // 1) 정확 일치 (username) 우선
        const exactUsername = sellersOnly.find(acc => acc.username.toLowerCase() === searchLower);
        if (exactUsername) {
          params.sellerId = exactUsername.id;
          break;
        }

        // 2) 정확 일치 (name)
        const exactName = sellersOnly.find(acc => acc.name.toLowerCase() === searchLower);
        if (exactName) { params.sellerId = exactName.id; break; }

        // 3) 숫자인 경우 ID로 검색 (단, 완전히 숫자인 경우만)
        if (/^\d+$/.test(trimmed)) {
          params.sellerId = parseInt(trimmed);
          break;
        }

        // 4) prefix 일치 (username)
        const startsUser = sellersOnly.filter(acc => acc.username.toLowerCase().startsWith(searchLower));
        if (startsUser.length > 0) {
          startsUser.sort((a, b) => a.username.length - b.username.length || a.id - b.id);
          params.sellerId = startsUser[0].id; break;
        }

        // 5) prefix 일치 (name)
        const startsName = sellersOnly.filter(acc => acc.name.toLowerCase().startsWith(searchLower));
        if (startsName.length > 0) {
          startsName.sort((a, b) => a.name.length - b.name.length || a.id - b.id);
          params.sellerId = startsName[0].id; break;
        }

        // 6) 부분 일치 (username)
        const includesUser = sellersOnly.filter(acc => acc.username.toLowerCase().includes(searchLower));
        if (includesUser.length > 0) {
          includesUser.sort((a, b) => {
            const ia = a.username.toLowerCase().indexOf(searchLower);
            const ib = b.username.toLowerCase().indexOf(searchLower);
            if (ia !== ib) return ia - ib; // 앞쪽 매치 우선
            return a.username.length - b.username.length || a.id - b.id;
          });
          params.sellerId = includesUser[0].id; break;
        }

        // 7) 부분 일치 (name)
        const includesName = sellersOnly.filter(acc => acc.name.toLowerCase().includes(searchLower));
        if (includesName.length > 0) {
          includesName.sort((a, b) => {
            const ia = a.name.toLowerCase().indexOf(searchLower);
            const ib = b.name.toLowerCase().indexOf(searchLower);
            if (ia !== ib) return ia - ib;
            return a.name.length - b.name.length || a.id - b.id;
          });
          params.sellerId = includesName[0].id; break;
        }

        // 매치가 하나도 없으면 -1을 넣어 빈 결과가 나오도록 유도
        params.sellerId = -1;
        message.warning('존재하지 않는 셀러입니다.');
        break;
      }
      case 'creator': {
        const trimmed = value.trim();
        const searchLower = trimmed.toLowerCase();
        const allAccounts = accounts;

        // 1) 정확 일치 (username) 우선
        const exactUsername = allAccounts.find(acc => acc.username.toLowerCase() === searchLower);
        if (exactUsername) {
          params.creatorId = exactUsername.id;
          break;
        }

        // 2) 정확 일치 (name)
        const exactName = allAccounts.find(acc => acc.name.toLowerCase() === searchLower);
        if (exactName) { params.creatorId = exactName.id; break; }

        // 3) 숫자인 경우 ID로 검색 (단, 완전히 숫자인 경우만)
        if (/^\d+$/.test(trimmed)) {
          params.creatorId = parseInt(trimmed);
          break;
        }

        // 4) prefix 일치 (username)
        const startsUser = allAccounts.filter(acc => acc.username.toLowerCase().startsWith(searchLower));
        if (startsUser.length > 0) {
          startsUser.sort((a, b) => a.username.length - b.username.length || a.id - b.id);
          params.creatorId = startsUser[0].id; break;
        }

        // 5) prefix 일치 (name)
        const startsName = allAccounts.filter(acc => acc.name.toLowerCase().startsWith(searchLower));
        if (startsName.length > 0) {
          startsName.sort((a, b) => a.name.length - b.name.length || a.id - b.id);
          params.creatorId = startsName[0].id; break;
        }

        // 6) 부분 일치 (username)
        const includesUser = allAccounts.filter(acc => acc.username.toLowerCase().includes(searchLower));
        if (includesUser.length > 0) {
          includesUser.sort((a, b) => {
            const ia = a.username.toLowerCase().indexOf(searchLower);
            const ib = b.username.toLowerCase().indexOf(searchLower);
            if (ia !== ib) return ia - ib; // 앞쪽 매치 우선
            return a.username.length - b.username.length || a.id - b.id;
          });
          params.creatorId = includesUser[0].id; break;
        }

        // 7) 부분 일치 (name)
        const includesName = allAccounts.filter(acc => acc.name.toLowerCase().includes(searchLower));
        if (includesName.length > 0) {
          includesName.sort((a, b) => {
            const ia = a.name.toLowerCase().indexOf(searchLower);
            const ib = b.name.toLowerCase().indexOf(searchLower);
            if (ia !== ib) return ia - ib;
            return a.name.length - b.name.length || a.id - b.id;
          });
          params.creatorId = includesName[0].id; break;
        }

        // 매치가 하나도 없으면 -1을 넣어 빈 결과가 나오도록 유도
        params.creatorId = -1;
        message.warning('존재하지 않는 생성자입니다.');
        break;
      }
      case 'mid_single':
        // MID 검색 처리
        params.mid_single = trimmed;
        break;
      case 'mid_compare':
        // MID 검색 처리
        params.mid_compare = trimmed;
        break;
    }

    // MID 검색 처리 (searchField가 'mid'일 때만)
    if (searchField === 'mid_single' && trimmed) {
      params.mid_single = trimmed;
    }
    if (searchField === 'mid_compare' && trimmed) {
      params.mid_compare = trimmed;
    }

    // 항상 활성 상태만 조회
    params.active = 'true';

    updateSearchParams(params);
  }, [accounts, searchField, updateSearchParams]);

  // 페이지 변경
  const handlePageChange = useCallback((page: number, pageSize?: number) => {
    setSearchParams(prev => ({
      ...prev,
      page,
      pageSize: pageSize ?? prev.pageSize
    }));
  }, []);

  // 페이지 변경 시 화면을 위로 이동
  useEffect(() => {
    if (searchParams.page && searchParams.page > 1) {
      // ref를 사용하여 최상단으로 스크롤
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [searchParams.page]);

  /* ==============  선택 슬롯 정보 ============== */
  const selectedSlots = useMemo(
    () => displaySlots.filter((s) => selected.includes(s.slotId)),
    [displaySlots, selected],
  );

  /* ==============  공통 성공 콜백 ============== */
  const handleSuccess = useCallback((msg: string) => {
    message.success(msg);
    setSelected([]);
  }, []);

  /* ==============  CRUD / 배치 ============== */
  const onCreate = useCallback(
    (dto: CreateSlotDTO): Promise<boolean> => {
      if (!me) return Promise.resolve(false);

      const { optimisticSlots, tempIds } = prepareOptimisticBatchCreate(
        { count: 1, slots: [dto] },
        me,
        accounts,
      );
      dispatch(beginOptimisticCreateSlots({ slots: optimisticSlots }));
      handleSuccess('슬롯이 생성되었습니다.');

      dispatch(createSlot({ dto, me, tempId: tempIds[0] }))
        .unwrap()
        .then((slot) => {
          if (expectsAsyncRank(slot) || expectsAsyncRank(dto)) {
            scheduleSlotRankPolling(dispatch, [slot.slotId]);
          }
        })
        .catch(() => {
          message.error('슬롯 생성에 실패했습니다.');
        });

      return Promise.resolve(true);
    },
    [dispatch, me, handleSuccess, accounts],
  );

  const onExtend = useCallback(
    (days: number): Promise<void> => {
      if (!me) return Promise.resolve();
      const ids = selected as number[];
      const snapshots: Record<number, Slot> = {};
      for (const id of ids) {
        const row = displaySlots.find((s) => s.slotId === id);
        if (row) snapshots[id] = row;
      }
      dispatch(beginOptimisticBatchExtend({ ids, days, snapshots }));
      return dispatch(
        batchExtend({ ids, days, me, optimisticAlreadyApplied: true }),
      )
        .unwrap()
        .then(() => {
          handleSuccess('연장 완료되었습니다.');
          return Promise.resolve();
        })
        .catch((error) => {
          message.error('연장 실패하였습니다.');
          return Promise.reject(error);
        });
    },
    [dispatch, selected, me, handleSuccess, displaySlots],
  );

  const onRefund = useCallback((): Promise<void> => {
    if (!me) return Promise.resolve();
    const ids = selected as number[];
    const snapshots: Record<number, Slot> = {};
    for (const id of ids) {
      const row = displaySlots.find((s) => s.slotId === id);
      if (row) snapshots[id] = row;
    }
    dispatch(beginOptimisticBatchRefund({ ids, snapshots }));
    return dispatch(batchRefund({ ids, me, optimisticAlreadyApplied: true }))
      .unwrap()
      .then(() => {
        handleSuccess('환불 완료되었습니다.');
        return Promise.resolve();
      })
      .catch((error) => {
        message.error('환불 실패하였습니다.');
        return Promise.reject(error);
      });
  }, [dispatch, selected, me, handleSuccess, displaySlots]);

  const onUpdate = useCallback(
    (id: number, dto: Partial<Slot>) => {
      if (!me) return Promise.resolve();
      const current = displaySlots.find((s) => s.slotId === id);
      const useOptimistic = Boolean(current && isOptimisticInlineDto(dto));
      if (useOptimistic && current) {
        dispatch(
          beginOptimisticSlotMerge({ id, snapshot: current, patch: dto }),
        );
      }
      return dispatch(updateSlot({ id, dto, me }))
        .unwrap()
        .then((slot) => {
          // 서버가 준 슬롯으로 reducer가 list·defaultPage.slots를 이미 갱신함 — 전체 재조회 불필요
          handleSuccess('수정 완료되었습니다.');
          if (dtoTriggersRankRecalc(dto)) {
            scheduleSlotRankPolling(dispatch, [slot.slotId]);
          }
          return Promise.resolve();
        })
        .catch((error) => {
          message.error('수정 실패하였습니다.');
          return Promise.reject(error);
        });
    },
    [dispatch, me, handleSuccess, displaySlots],
  );

  // 슬롯들 모두 업데이트
  const onBulkUpdate = useCallback(
    async (dto: Partial<Slot>) => {
      if (!me) return;

      /* 각 슬롯 업데이트 */
      const results = await Promise.all(
        (selected as number[]).map(id =>
          dispatch(updateSlot({ id, dto, me }))
            .unwrap()
            .then((slot) => ({ ok: true as const, id: slot.slotId }))
            .catch(() => ({ ok: false as const, id })),
        ),
      );

      /* 집계 */
      const okIds = results.filter((r) => r.ok).map((r) => r.id);
      const ok = okIds.length;
      const err = results.length - ok;

      if (ok && !err) {
        message.success(`${ok}개 슬롯 수정 완료`);
      }
      else if (ok && err) {
        message.warning(`${ok}개 성공, ${err}개 실패`);
      }
      else {
        message.error('슬롯 수정 실패');
      }

      if (okIds.length && dtoTriggersRankRecalc(dto)) {
        scheduleSlotRankPolling(dispatch, okIds);
      }

      setSelected([]);
    },
    [dispatch, me, selected],
  );

  /* ---------------- 삭제 ---------------- */
  const onDelete = useCallback(() => {
    if (!me) return;
    try {
      assertAdminOrManager(me);
    } catch (e) {
      message.error((e as Error).message);
      return;
    }

    const ids = (selected as number[]).filter((id) => id > 0);
    if (!ids.length) return;

    const snapshots: Record<number, Slot> = {};
    for (const id of ids) {
      const row = displaySlots.find((s) => s.slotId === id);
      if (row) snapshots[id] = row;
    }

    dispatch(beginOptimisticDeleteSlots({ ids, snapshots }));
    handleSuccess(`${ids.length}개 슬롯이 삭제되었습니다.`);

    Promise.all(
      ids.map((id) =>
        dispatch(deleteSlot({ id, me, optimisticAlreadyApplied: true }))
          .unwrap()
          .then(() => true)
          .catch(() => false),
      ),
    ).then((results) => {
      const failCount = results.filter((ok) => !ok).length;
      if (failCount > 0) {
        message.warning(`${ids.length - failCount}개 삭제 완료, ${failCount}개 실패`);
      }
    });
  }, [me, selected, dispatch, handleSuccess, displaySlots]);

  /* ---------------- 일괄 생성 ---------------- */
  const onBatchCreate = useCallback(
    (request: { count: number; slots: CreateSlotDTO[] }): Promise<void> => {
      if (!me) return Promise.resolve();

      const { optimisticSlots, tempIds } = prepareOptimisticBatchCreate(
        request,
        me,
        accounts,
      );
      dispatch(beginOptimisticCreateSlots({ slots: optimisticSlots }));
      handleSuccess(`${request.count}개의 슬롯이 생성되었습니다.`);

      dispatch(
        batchCreateSlots({
          request,
          me,
          tempIds,
          optimisticAlreadyApplied: true,
        }),
      )
        .unwrap()
        .then((slots) => {
          const ids = slots
            .filter((s) => expectsAsyncRank(s))
            .map((s) => s.slotId);
          if (ids.length) scheduleSlotRankPolling(dispatch, ids);
        })
        .catch(() => {
          message.error('슬롯 일괄 생성에 실패했습니다.');
        });

      return Promise.resolve();
    },
    [dispatch, me, handleSuccess, accounts],
  );

  const onCloneCreate = useCallback(
    (request: { count: number; slots: CreateSlotDTO[] }): Promise<void> => {
      if (!me || !request.slots.length) return Promise.resolve();

      if (request.count === 1) {
        const dto = request.slots[0];
        const { optimisticSlots, tempIds } = prepareOptimisticBatchCreate(
          { count: 1, slots: [dto] },
          me,
          accounts,
        );
        dispatch(beginOptimisticCreateSlots({ slots: optimisticSlots }));
        handleSuccess('복제 슬롯이 생성되었습니다');

        dispatch(createSlot({ dto, me, tempId: tempIds[0] }))
          .unwrap()
          .then((slot) => {
            if (expectsAsyncRank(slot) || expectsAsyncRank(dto)) {
              scheduleSlotRankPolling(dispatch, [slot.slotId]);
            }
          })
          .catch(() => {
            message.error('복제 슬롯 생성에 실패했습니다.');
          });

        return Promise.resolve();
      }

      const { optimisticSlots, tempIds } = prepareOptimisticBatchCreate(
        request,
        me,
        accounts,
      );
      dispatch(beginOptimisticCreateSlots({ slots: optimisticSlots }));
      handleSuccess('복제 슬롯이 생성되었습니다');

      dispatch(
        batchCreateSlots({
          request,
          me,
          tempIds,
          optimisticAlreadyApplied: true,
        }),
      )
        .unwrap()
        .then((slots) => {
          const ids = slots
            .filter((s) => expectsAsyncRank(s))
            .map((s) => s.slotId);
          if (ids.length) scheduleSlotRankPolling(dispatch, ids);
        })
        .catch(() => {
          message.error('복제 슬롯 일괄 생성에 실패했습니다.');
        });

      return Promise.resolve();
    },
    [dispatch, me, accounts, handleSuccess],
  );


  /* ---------------- 엑셀 다운로드 ---------------- */
  const [legacyExcelDownloading, setLegacyExcelDownloading] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);

  const handleLegacyExcelDownload = useCallback(async () => {
    if (!me) return;
    setLegacyExcelDownloading(true);
    try {
      const slotsToExport = selected.length > 0
        ? selectedSlots
        : (await dispatch(fetchAllSlots({ me, active: true })).unwrap()).slots;
      if (slotsToExport.length === 0) {
        message.warning('다운로드할 슬롯이 없습니다.');
        return;
      }
      const { exportSlotsToExcel } = await import('../../Utils/exportSlotsToExcel');
      exportSlotsToExcel(slotsToExport, accounts, selected.length === 0);
      message.success(`${slotsToExport.length}개 슬롯이 다운로드되었습니다.`);
    } catch {
      message.error('기존 엑셀 다운로드에 실패했습니다.');
    } finally {
      setLegacyExcelDownloading(false);
    }
  }, [accounts, dispatch, me, selected.length, selectedSlots]);
  
  const handleExcelDownload = useCallback(async () => {
    if (!me) return;
    setExcelDownloading(true);
    try {
      if (selected.length > 0 && selectedSlots.length === 0) {
        message.warning('다운로드할 슬롯이 없습니다.');
        return;
      }

      const { data: job } = await api.post('/slots/excel/export-jobs/',
        selected.length > 0
          ? { ids: selectedSlots.map((slot) => slot.slotId) }
          : { active: true },
      );
      message.loading({ content: `상품명 추출 엑셀을 생성 중입니다. (${job.slot_count}개)`, key: 'product-excel' });

      let jobStatus = job.status;
      while (jobStatus === 'pending' || jobStatus === 'running') {
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
        const { data } = await api.get(`/slots/excel/export-jobs/${job.job_id}/`);
        jobStatus = data.status;
        if (jobStatus === 'failed') {
          throw new Error(data.error || '상품명 엑셀 생성에 실패했습니다.');
        }
      }
      if (jobStatus !== 'complete') {
        throw new Error('상품명 엑셀 생성 상태를 확인할 수 없습니다.');
      }

      const response = await api.get(`/slots/excel/export-jobs/${job.job_id}/download/`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'slots_product_export.xlsx';
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      message.success({
        key: 'product-excel',
        content:
        selected.length > 0
          ? `${selectedSlots.length}개 슬롯이 다운로드되었습니다.`
          : '전체 활성 슬롯 엑셀 다운로드가 완료되었습니다.',
      });
    } catch (error) {
      message.error({
        key: 'product-excel',
        content: error instanceof Error ? error.message : '엑셀 다운로드에 실패했습니다.',
      });
    } finally {
      setExcelDownloading(false);
    }
  }, [me, selected.length, selectedSlots]);

  // 엑셀 업로드(사전 파싱 경로): 파일만 받아 모달을 연다
  const openExcelImport = useCallback((file: File) => {
    setExcelImportFile(file);
    setExcelImportOpen(true);
  }, []);

  const openExcelBatchUpdate = useCallback((file: File) => {
    setExcelBatchUpdateFile(file);
    setExcelBatchUpdateOpen(true);
  }, []);

  const uploadChunkWithOptimistic = useCallback(
    async (chunk: CreateSlotDTO[], chunkIndex: number, totalChunks: number) => {
      if (!me) return 0;

      const serverRequest = { count: 1, slots: chunk };
      const { optimisticSlots, tempIds } = prepareOptimisticBatchCreate(
        serverRequest,
        me,
        accounts,
      );
      dispatch(beginOptimisticCreateSlots({ slots: optimisticSlots }));

      message.loading({
        content: `${chunkIndex + 1}/${totalChunks} 묶음 등록 중... (${chunk.length}개)`,
        key: 'batchUpload',
        duration: 0,
      });

      try {
        const created = await dispatch(
          batchCreateSlots({
            request: serverRequest,
            me,
            tempIds,
            optimisticAlreadyApplied: true,
          }),
        ).unwrap();
        const pollIds = created
          .filter((s) => expectsAsyncRank(s))
          .map((s) => s.slotId);
        if (pollIds.length) scheduleSlotRankPolling(dispatch, pollIds);
        return chunk.length;
      } catch {
        message.error(`${chunkIndex + 1}번째 묶음 업로드 실패`);
        return 0;
      }
    },
    [dispatch, me, accounts],
  );

  const waitBetweenChunks = useCallback(async (seconds: number) => {
    for (let sec = seconds; sec > 0; sec--) {
      setBatchUploadWaitSeconds(sec);
      message.loading({
        content: `서버 부하 방지 대기 중... 다음 묶음까지 ${sec}초`,
        key: 'batchUpload',
        duration: 0,
      });
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    setBatchUploadWaitSeconds(0);
  }, []);

  const handleExcelImportSubmit = useCallback(
    async (request: { count: number; slots: CreateSlotDTO[] }) => {
      if (!me) return;

      const CHUNK_SIZE = 20;
      const DELAY_SEC = 30;

      setExcelImportOpen(false);
      setExcelImportFile(null);

      const allSlots = request.slots;
      const totalCount = allSlots.length;

      if (totalCount <= CHUNK_SIZE) {
        const serverRequest = { count: 1, slots: allSlots };
        const { optimisticSlots, tempIds } = prepareOptimisticBatchCreate(
          serverRequest,
          me,
          accounts,
        );
        dispatch(beginOptimisticCreateSlots({ slots: optimisticSlots }));
        message.success(`${totalCount}개 슬롯 등록을 시작했습니다.`);

        dispatch(
          batchCreateSlots({
            request: serverRequest,
            me,
            tempIds,
            optimisticAlreadyApplied: true,
          }),
        )
          .unwrap()
          .then((slots) => {
            message.success(`${totalCount}개 슬롯 등록이 완료되었습니다.`);
            const pollIds = slots
              .filter((s) => expectsAsyncRank(s))
              .map((s) => s.slotId);
            if (pollIds.length) scheduleSlotRankPolling(dispatch, pollIds);
          })
          .catch(() => {
            message.error('슬롯 생성에 실패했습니다.');
          });
        return;
      }

      const chunks: CreateSlotDTO[][] = [];
      for (let i = 0; i < allSlots.length; i += CHUNK_SIZE) {
        chunks.push(allSlots.slice(i, i + CHUNK_SIZE));
      }

      setIsBatchUploading(true);
      setBatchUploadTotal(totalCount);
      setBatchUploadCurrent(0);
      setBatchUploadChunkTotal(chunks.length);
      setBatchUploadChunkCurrent(0);
      setBatchUploadWaitSeconds(0);

      message.info({
        content: `총 ${totalCount}개를 ${CHUNK_SIZE}개씩 ${chunks.length}묶음으로 등록합니다. 목록에 바로 표시되며 순위는 백그라운드에서 계산됩니다.`,
        key: 'batchUpload',
        duration: 4,
      });

      const runUpload = async () => {
        let successCount = 0;
        let failCount = 0;

        try {
          for (let i = 0; i < chunks.length; i++) {
            const chunkSuccess = await uploadChunkWithOptimistic(
              chunks[i],
              i,
              chunks.length,
            );
            if (chunkSuccess > 0) {
              successCount += chunkSuccess;
            } else {
              failCount += chunks[i].length;
            }

            setBatchUploadCurrent(successCount);
            setBatchUploadChunkCurrent(i + 1);

            if (i < chunks.length - 1) {
              await waitBetweenChunks(DELAY_SEC);
            }
          }
        } finally {
          setIsBatchUploading(false);
          setBatchUploadWaitSeconds(0);

          if (failCount === 0) {
            message.success({
              content: `총 ${successCount}개 슬롯 업로드가 모두 완료되었습니다.`,
              key: 'batchUpload',
              duration: 5,
            });
          } else {
            message.warning({
              content: `업로드 완료: 성공 ${successCount}개, 실패 ${failCount}개`,
              key: 'batchUpload',
              duration: 5,
            });
          }
        }
      };

      runUpload();
    },
    [dispatch, me, accounts, uploadChunkWithOptimistic, waitBetweenChunks],
  );

  const handleExcelBatchUpdateSubmit = useCallback(async (slots: Partial<Slot>[]) => {
    if (!me) return;
    setExcelBatchUpdateOpen(false);
    setExcelBatchUpdateFile(null);
    try {
      const realCount = slots.length;
      const res = await dispatch(batchUpdateSlots({ slots, me })).unwrap();
      dispatch(clearDefaultPage());
      
      const delayMs = Math.min(3000, Math.max(500, realCount * 100));
      setTimeout(() => { refresh(); }, delayMs);

      message.success(`${res.updated_count}개의 슬롯이 수정되었습니다.`);
      if (res.errors && res.errors.length > 0) {
        message.warning(`${res.errors.length}개의 오류가 발생했습니다.`);
      }

      const shouldPoll = slots.some((s) => dtoTriggersRankRecalc(s));
      if (shouldPoll && res.updated_ids?.length) {
        scheduleSlotRankPolling(dispatch, res.updated_ids);
      }
    } catch (error) {
      message.error('엑셀 기반 일괄 수정에 실패했습니다.');
    }
  }, [dispatch, me, refresh]);

  /* ==============  에러 알림 ============== */
  useEffect(() => { if (error) message.error(error); }, [error]);

  /* ==============  아직 사용자 정보가 없으면 스피너 ============== */
  if (!me) {
    return <Spin style={{ display: 'block', margin: '160px auto' }} />;
  }

  /* ==============  JSX  ============== */
  return (
    <div ref={topRef}>
      <Card title="슬롯 관리" bodyStyle={{ padding: 0 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {isBatchUploading && (
            <div style={{ padding: '16px 16px 0 16px' }}>
              <Alert
                message="분할 업로드 진행 중"
                description={
                  <div style={{ marginTop: 8 }}>
                    <Text>
                      슬롯 {batchUploadCurrent}/{batchUploadTotal}개 완료
                      {batchUploadChunkTotal > 0 &&
                        ` · 묶음 ${batchUploadChunkCurrent}/${batchUploadChunkTotal}`}
                    </Text>
                    <Progress
                      percent={
                        batchUploadTotal > 0
                          ? Math.floor((batchUploadCurrent / batchUploadTotal) * 100)
                          : 0
                      }
                      status="active"
                      strokeColor="#B03052"
                    />
                    {batchUploadWaitSeconds > 0 ? (
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                        다음 묶음 전송까지 {batchUploadWaitSeconds}초 남음 (서버 부하 방지)
                      </Text>
                    ) : (
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                        새 슬롯은 목록에 바로 표시됩니다. 순위는 계산 완료 후 자동 갱신됩니다.
                      </Text>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                      * 브라우저 창을 닫거나 새로고침하면 업로드가 중단될 수 있습니다.
                    </Text>
                  </div>
                }
                type="info"
                showIcon
              />
            </div>
          )}
          <div style={{ padding: '0 16px' }}>
            <SlotOpsOverrideBanner />
          </div>
          <ManagementBar
            /* 선택 & 액션 */
            selected={selected}
            selectedSlots={selectedSlots}
            onAdd={() => setAddOpen(true)}
            onClone={() => setCloneOpen(true)}
            onExtend={() => setExtOpen(true)}
            onEdit={() => setEditOpen(true)}
            onRefund={() => setRefundOpen(true)}
            onDelete={onDelete}
            /* 검색 / 상태 */
            onSearch={handleSearch}
            status=""
            onStatusChange={() => {}}
            searchField={searchField}
            onSearchFieldChange={handleSearchFieldChange}
            localFindOpen={localFindOpen}
            localFindMatchCount={localFindMatchCount}
            localFindActiveMatchIndex={localFindActiveMatchIndex}
            localFindInputRef={localFindInputRef}
            onCloseLocalFind={closeLocalFind}
            onLocalFindQueryChange={handleLocalFindChange}
            onLocalFindNext={() => handleLocalFindNavigate(1)}
            onLocalFindPrev={() => handleLocalFindNavigate(-1)}
            /* 정렬 */
            sortBy={searchParams.order_by || 'registered'}
            sortDir={searchParams.order_dir || 'desc'}
            onSortByChange={handleSortByChange}
            onSortDirChange={handleSortDirChange}
            onBatchEdit={() => setBulkEditOpen(true)}
            /* 엑셀 */
            onExcelParsedUpload={openExcelImport}
            onExcelBatchUpdateUpload={openExcelBatchUpdate}
            onLegacyExcelDownload={handleLegacyExcelDownload}
            onExcelDownload={handleExcelDownload}
            excelUploading={excelUploading}
            legacyExcelDownloading={legacyExcelDownloading}
            excelDownloading={excelDownloading}
          />

          <SlotTable
            data={displaySlots}
            loading={loading}
            selected={selected}
            onSelectChange={setSelected}
            onInlineUpdate={onUpdate}
            onClone={(slot) => {
              setSelected([slot.slotId]);
              setCloneOpen(true);
            }}
            localFindQuery={localFindOpen ? localFindQuery : ''}
            localFindMatchIndex={localFindActiveMatchIndex}
            onLocalFindMatchCountChange={setLocalFindMatchCount}
            pagination={displayPagination || undefined}
            onPageChange={handlePageChange}
          />
        </Space>

        {/* ---------- 모달 ---------- */}
        <SlotAddModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onSubmit={onCreate}
          onBatchSubmit={onBatchCreate}
        />

        <SlotCloneModal
          open={cloneOpen}
          sourceSlot={selectedSlots.length === 1 ? selectedSlots[0] : null}
          onClose={() => setCloneOpen(false)}
          onSubmit={onCloneCreate}
        />

        <SlotExtendModal
          open={extOpen}
          onClose={() => setExtOpen(false)}
          onSubmit={onExtend}
        />

        <SlotEditModal
          open={editOpen}
          slotIds={selected as number[]}
          onClose={() => setEditOpen(false)}
          onSubmit={dto => Promise.all(
            (selected as number[]).map(id => onUpdate(id, dto)),
          ).then(() => undefined)}
        />

        <SlotRefundModal
          open={refundOpen}
          onClose={() => setRefundOpen(false)}
          onSubmit={onRefund}
        />

        <SlotBulkEditModal
          open={bulkEditOpen}
          slotCount={selected.length}
          onClose={() => setBulkEditOpen(false)}
          onSubmit={onBulkUpdate}
        />

        <ExcelImportModal
          open={excelImportOpen}
          file={excelImportFile}
          onClose={() => { setExcelImportOpen(false); setExcelImportFile(null); }}
          onSubmit={handleExcelImportSubmit}
        />

        <ExcelBatchUpdateModal
          open={excelBatchUpdateOpen}
          file={excelBatchUpdateFile}
          onClose={() => { setExcelBatchUpdateOpen(false); setExcelBatchUpdateFile(null); }}
          onSubmit={handleExcelBatchUpdateSubmit}
        />
      </Card>
    </div>
  );
};

export default SlotsPage;
